import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, AppRole } from '@/contexts/AuthContext';
import { useMyClubStaff } from '@/hooks/organizations/useMyClubStaff';
import { usePendingPayment } from '@/hooks/payments/usePendingPayment';
import { LottieSpinner } from '@/components/ui/LottieSpinner';
import { BlockingPage } from './BlockingPage';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: AppRole[];
}

export function ProtectedRoute({ children, requiredRoles }: ProtectedRouteProps) {
  const { user, loading, rolesLoading, roles, profile, subscription, subscriptionLoading, signOut } = useAuth();
  const { myStaffRoles } = useMyClubStaff();
  const { pendingPayment, isLoading: pendingPaymentLoading } = usePendingPayment();
  const location = useLocation();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth');
  };

  if (loading) {
    return <LottieSpinner />;
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // Tant que les rôles ne sont pas chargés, `roles` vaut `[]` : tout ce qui
  // suit le lirait comme « aucun rôle » et renverrait vers l'onboarding.
  if (rolesLoading) {
    return <LottieSpinner />;
  }

  // Les super_admin et admin peuvent bypasser la vérification d'abonnement
  const isSuperAdmin = roles.some(r => ['super_admin', 'admin'].includes(r.role));

  // Vérifier si l'utilisateur a un rôle professionnel avec abonnement requis
  // tournament_organizer est exclu car c'est un paiement one-shot, pas un abonnement
  // super_admin et admin sont également exclus car ils n'ont pas besoin d'abonnement
  // Board members (coordinator role via bureau sync) are covered by the org admin's subscription
  const isOnlyBoardMember = myStaffRoles.length > 0 &&
    myStaffRoles.every(r => r.role === 'coordinator');

  const isProfessional = !isSuperAdmin && !isOnlyBoardMember && roles.some(r =>
    ['coach', 'referee', 'club_staff', 'association_staff'].includes(r.role)
  );
  
  if (isProfessional) {
    // Attendre que la subscription soit chargée avant de vérifier
    if (subscriptionLoading) {
      // console.log('⏳ Waiting for subscription to load...');
      return <LottieSpinner />;
    }

    // Vérifier que le rôle professionnel a une subscription correspondante
    const professionalRoles = roles.filter(r => 
      ['coach', 'referee', 'club_staff', 'association_staff'].includes(r.role)
    );
    
    // Check if any subscribed role matches the user's professional roles
    const subscribedRoles = subscription?.roles || (subscription?.role ? [subscription.role] : []);
    const hasMatchingSubscription = subscription?.subscribed && 
      professionalRoles.some(r => subscribedRoles.includes(r.role));
    
    // console.log('🔒 Subscription Check:', {
    //   professionalRoles: professionalRoles.map(r => r.role),
    //   subscribedRoles,
    //   subscribed: subscription?.subscribed,
    //   hasMatchingSubscription
    // });

    if (!hasMatchingSubscription) {
      // Attendre que le paiement en attente soit chargé
      if (pendingPaymentLoading) {
        return <LottieSpinner />;
      }

      // Si un paiement par virement est en attente, rediriger vers la page de confirmation
      if (pendingPayment && !location.pathname.startsWith('/onboarding/bank-transfer')) {
        return <Navigate to={`/onboarding/bank-transfer/${pendingPayment.id}`} replace />;
      }

      // Sinon, rediriger vers la sélection de plan correspondant au rôle pro
      const ROLE_TO_PLAN_ROUTE: Record<string, string> = {
        coach: '/onboarding/register/coach',
        referee: '/onboarding/register/referee',
        club_staff: '/onboarding/register/club',
        association_staff: '/onboarding/register/association',
      };

      const firstProRole = professionalRoles[0]?.role;
      const planRoute = ROLE_TO_PLAN_ROUTE[firstProRole];

      if (planRoute && !location.pathname.startsWith('/onboarding')) {
        return <Navigate to={planRoute} state={{ from: location }} replace />;
      }

      // Fallback: si on est déjà sur /onboarding ou pas de route trouvée, bloquer
      return (
        <BlockingPage
          title="Abonnement requis"
          message="Un abonnement actif est requis pour accéder à cette fonctionnalité professionnelle."
          actions={[
            {
              label: 'Choisir un plan',
              onClick: () => navigate(planRoute || '/onboarding/register'),
            },
            { label: 'Me déconnecter', onClick: handleSignOut, variant: 'outline' },
          ]}
        />
      );
    }
    
    // console.log('✅ Valid subscription found, allowing access');
  }

  // Redirect to profile completion only if profile is truly incomplete
  // (avoid treating undefined/null as incomplete when profile is already complete in DB)
  if (!profile) {
    return <LottieSpinner />;
  }

  // Check profile completion for players or new users with no roles yet
  // Users who only have professional roles (association_staff, club_staff, coach, etc.)
  // should not be forced to complete a player profile
  const hasPlayerRole = roles.some(r => r.role === 'player');
  const hasOnlyProfessionalRoles = roles.length > 0 && !hasPlayerRole;

  const isStaffRoute = location.pathname.startsWith('/association/') ||
                       location.pathname.startsWith('/club/') ||
                       location.pathname.startsWith('/admin/') ||
                       location.pathname.startsWith('/coach/') ||
                       location.pathname.startsWith('/tournament-manager/') ||
                       location.pathname === '/settings';

  const isProfileCompleted =
    profile.profile_completed === true ||
    (!!profile.date_of_birth &&
      !!profile.gender &&
      !!profile.preferred_position &&
      !!profile.skill_level);

  // Redirect to profile completion for players or new users with no roles (not staff-only users)
  if (!hasOnlyProfessionalRoles && !isStaffRoute && !isProfileCompleted && location.pathname !== '/onboarding/complete-profile') {
    return <Navigate to="/onboarding/complete-profile" replace />;
  }

  // Debug logs désactivés pour améliorer les performances

  if (requiredRoles && requiredRoles.length > 0) {
    const hasRequiredRole = requiredRoles.some(role =>
      roles.some(r => r.role === role)
    );

    if (!hasRequiredRole) {
      // If user has NO roles at all, redirect to complete-profile to re-trigger role creation
      if (roles.length === 0) {
        return <Navigate to="/onboarding/complete-profile" replace />;
      }
      return (
        <BlockingPage
          title="Accès refusé"
          message="Vous n'avez pas les permissions nécessaires pour accéder à cette page."
        />
      );
    }

  }

  return <>{children}</>;
}
